import React, { useCallback, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import color from "@constants/color";
import Text from "@components/CustomText";
import VerifiedBadge from "@assets/svg/VerifiedBadge";
import LocationPin from "@assets/svg/LocationPin";
import EditIcon from "@assets/svg/EditIcon";
import { fontFamily } from "@utils/styles/fonts";
import type { RouteProp } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import { getOnboardingStatus } from "@services/onboardingApi";
import type { RejectedKycItem } from "@services/onboardingApi";
import type { OnboardingStackParamList } from "./types";

const KYC_STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  CREATED: { label: "Pending", color: "#888888", bg: "#F5F5F5" },
  PROCESSING: { label: "Under Review", color: "#E07B00", bg: "#FFF5E6" },
  VERIFIED: { label: "Verified", color: "#1A7F37", bg: "#E6F6EC" },
  FAILED: { label: "Failed", color: "#C41A16", bg: "#FFE1E5" },
  REJECTED: { label: "Rejected", color: "#C41A16", bg: "#FFE1E5" },
};

function KycStatusPill({ status }: { status: string }) {
  const cfg = KYC_STATUS_CONFIG[status] ?? {
    label: status,
    color: "#888888",
    bg: "#F5F5F5",
  };
  return (
    <View style={[pillStyles.pill, { backgroundColor: cfg.bg }]}>
      <Text style={pillStyles.label}>KYC Status: </Text>
      <Text style={[pillStyles.value, { color: cfg.color }]}>{cfg.label}</Text>
    </View>
  );
}

const pillStyles = StyleSheet.create({
  pill: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "center",
    borderRadius: 24,
    paddingHorizontal: 18,
    paddingVertical: 10,
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontFamily: fontFamily.regular,
    color: color.Gray20,
  },
  value: {
    fontSize: 14,
    fontFamily: fontFamily.semibold ?? fontFamily.medium,
  },
});

interface ApplicationSubmittedProps {
  route: RouteProp<OnboardingStackParamList, "ApplicationSubmitted">;
  navigation: StackNavigationProp<
    OnboardingStackParamList,
    "ApplicationSubmitted"
  >;
}

const ApplicationSubmitted: React.FC<ApplicationSubmittedProps> = ({
  route,
  navigation,
}) => {
  const { spokeName, spokeAddress, fullName } = route.params;
  const [canChangeSpoke, setCanChangeSpoke] = useState(true);
  const [kycStatus, setKycStatus] = useState<string | null>(null);
  const [isFamilyKyc, setIsFamilyKyc] = useState(false);
  const [dbType, setDbType] = useState<"NORMAL" | "LAGGAN">("NORMAL");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [rejectedItems, setRejectedItems] = useState<RejectedKycItem[]>([]);
  const [aadhaarNumber, setAadhaarNumber] = useState<string | null>(null);
  const [panNumber, setPanNumber] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      getOnboardingStatus()
        .then((status) => {
          setCanChangeSpoke(status.kyc_status !== "VERIFIED");
          setKycStatus(status.kyc_status ?? null);
          setIsFamilyKyc(status.is_family_kyc ?? false);
          setDbType(status.db_type ?? "NORMAL");
          setPhoneNumber(status.phone_number ?? "");
          setRejectedItems(status.rejected_items ?? []);
          setAadhaarNumber(status.aadhaar_number ?? null);
          setPanNumber(status.pan_number ?? null);
        })
        .catch(() => {});
    }, []),
  );

  return (
    <>
      <ScrollView
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.badgeWrapper}>
          <VerifiedBadge size={120} />
        </View>

        <Text variant="heading_24" style={styles.title}>
          Application Submitted!
        </Text>
        <Text variant="body_text_1" style={styles.subtitle}>
          {fullName ? `Congratulations ${fullName}` : "Congratulations"}
        </Text>

        {kycStatus && <KycStatusPill status={kycStatus} />}

        {kycStatus === "REJECTED" && (
          <Pressable
            style={styles.reuploadButton}
            onPress={() =>
              navigation.navigate("ReuploadDocuments", {
                isFamilyKyc,
                dbType,
                phoneNumber,
                rejectedItems,
                aadhaarNumber,
                panNumber,
              })
            }
            accessibilityRole="button"
            accessibilityLabel="Re-upload documents"
          >
            <Text variant="title_16" style={styles.reuploadButtonText}>
              Re-upload Documents
            </Text>
          </Pressable>
        )}

        {/* Spoke card */}
        <View style={styles.spokeCard}>
          <View style={styles.spokeCardTopRow}>
            <LocationPin size={18} color={color.Gray20} />
            <Text style={styles.spokeCardLabel}>Selected Spoke</Text>
            <View style={styles.spacer} />
            <Pressable
              onPress={() => navigation.navigate("NearestHub")}
              disabled={!canChangeSpoke}
              accessibilityRole="button"
              accessibilityLabel="Change spoke"
              hitSlop={8}
              style={canChangeSpoke ? styles.editIcon : styles.editIconDisabled}
            >
              <EditIcon width={16} height={16} />
            </Pressable>
          </View>
          <Text variant="title_16" style={styles.spokeName}>
            {spokeName}
          </Text>
          <Text variant="caption_2" style={styles.spokeAddress}>
            {spokeAddress}
          </Text>
        </View>

        {/* Next Step card */}
        <View style={styles.nextStepCard}>
          <Text style={styles.nextStepTitle}>Next Step</Text>
          <View style={styles.nextStepRow}>
            <View style={styles.nextStepBullet} />
            <Text variant="body_text_3" style={styles.nextStepText}>
              Please connect with the spoke Executive to confirm and start
              working with us.
            </Text>
          </View>
        </View>
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: color.white,
    alignItems: "center",
    paddingHorizontal: 24,
    paddingTop: 48,
    paddingBottom: 40,
  },
  badgeWrapper: {
    marginBottom: 28,
  },
  title: {
    color: color.Gray20,
    textAlign: "center",
    marginBottom: 8,
  },
  subtitle: {
    color: color.disableFont,
    textAlign: "center",
    marginBottom: 32,
  },
  spokeCard: {
    width: "100%",
    backgroundColor: color.white,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: color.lineColor,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: color.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
  },
  spokeCardTopRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  spokeCardLabel: {
    fontSize: 13,
    fontFamily: fontFamily.regular,
    color: color.disableFont,
  },
  spacer: {
    flex: 1,
  },
  spokeName: {
    color: color.Gray20,
    marginBottom: 4,
    paddingLeft: 2,
  },
  spokeAddress: {
    color: color.disableFont,
    lineHeight: 18,
    paddingLeft: 2,
  },
  nextStepCard: {
    width: "100%",
    backgroundColor: color.lightRed,
    borderRadius: 14,
    padding: 16,
  },
  nextStepTitle: {
    fontSize: 16,
    fontFamily: fontFamily.semibold ?? fontFamily.medium,
    color: color.Gray20,
    marginBottom: 10,
  },
  nextStepRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  nextStepBullet: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: color.primary,
    marginTop: 5,
  },
  nextStepText: {
    flex: 1,
    color: color.Gray20,
    lineHeight: 20,
  },
  editIcon: {
    opacity: 1,
  },
  editIconDisabled: {
    opacity: 0.3,
  },
  reuploadButton: {
    width: "100%",
    backgroundColor: color.errorRed,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
    marginBottom: 16,
  },
  reuploadButtonText: {
    color: color.white,
  },
});

export default ApplicationSubmitted;
